import type { Fraction } from '@mimir/common/util/fractionType';
import type { MimirItemRef, MimirVideoItemRef, MimirAudioItemRef } from './mimirItemRef';
import type { AudioSourceRef, ImageSourceRef, SourceRef, VideoSourceRef } from './sourceRef';
import { addFractions, subtractFractions } from '@mimir/common/util/integerMath';

interface ModelBox {

  /**
   * A unique ID.
   */
  boxId: string;

  /**
   * This range is relative to the beginning of the source
   * used by this object, like for instance a Mimir video item.
   * This range needs to be combined with the start position of
   * the source in order to get the absolute position from the
   * beginning of the timeline.
   */
  inPointInSeconds: Fraction;
  outPointInSeconds: Fraction;
}


export interface ModelVideoBox extends ModelBox {
  type: 'video';
  sourceRef: VideoSourceRef;
}


export interface ModelImageBox extends ModelBox {
  type: 'image';
  sourceRef: ImageSourceRef;
}


export interface ModelAudioBox extends ModelBox {
  type: 'audio';
  sourceRef: VideoSourceRef | AudioSourceRef;

  /**
   * The audio channel used by this object.
   */
  channel: number;

}


export type ModelVideoTrackBox = ModelVideoBox | ModelImageBox;
export type ModelAudioTrackBox = ModelAudioBox;

export type ModelTrackBox = ModelVideoTrackBox | ModelAudioTrackBox;

export type ModelVideoTrack = ModelVideoTrackBox[];
export type ModelAudioTrack = ModelAudioTrackBox[];


/**
 * This interface describes the internal state of the timeline.
 * It is the source of truth used to represent the timeline and
 * sanitize operations.
 *
 * This is essentially our de facto timeline format.
 */
export interface TimelineModel {

  /**
   * An internal counter that will be monotonically increased each time
   * something is added to the timeline, so that we get deterministic IDs
   * when invoking createUniqueId()
   */
  nextUiId: number;

  /**
   * All the Mimir items used in the timeline, indexed by item IDs.
   */
  mimirItemRefs: Record<string, MimirItemRef>;

  /**
   * How many times each item is referenced in the timeline.
   * When the count drops to 0, it means that the item is not used anymore.
   */
  mimirItemRefCounts: Record<string, number>;

  /**
   * All the source references used in the timeline, indexed by source IDs.
   */
  sourceRefs: Record<string, SourceRef>;

  /**
   * All the track boxes, indexed by box ID.
   */
  trackBoxesById: Record<string, ModelTrackBox>;

  /**
   * Maps each source ID to the track boxes related to this source.
   */
  trackBoxesBySourceId: Record<string, ModelTrackBox[]>;

  /**
   * The video tracks. By convention, track 0 is the main track and other
   * tracks are considered as overlays to be displayed, each one on top
   * of the previous one.
   */
  videoTracks: ModelVideoTrack[];

  /**
   * The audio tracks.
   * audioTracks[n] corresponds to the output channel n when rendering.
   */
  audioTracks: ModelAudioTrack[];
}


export function hasDuration(mimirItemRef: Pick<MimirItemRef, 'type'>): mimirItemRef is MimirVideoItemRef | MimirAudioItemRef {
  return mimirItemRef.type === 'video' || mimirItemRef.type === 'audio';
}


export function hasAudioChannels(mimirItemRef: Pick<MimirItemRef, 'type'>): mimirItemRef is MimirVideoItemRef | MimirAudioItemRef {
  return mimirItemRef.type === 'video' || mimirItemRef.type === 'audio';
}


/**
 * Returns the start position of the object in seconds from
 * the beginning of the timeline.
 */
export function getAbsoluteStartPositionInSeconds(audioBox: ModelAudioBox): Fraction {
  return addFractions(audioBox.inPointInSeconds, audioBox.sourceRef.mediaStartOffset);
}


export interface ModelTrackBoxLocation {
  trackType: 'videoTrack' | 'audioTrack';
  trackIndex: number;
  boxIndex: number;
}


export function isModelVideoTrackBox(box: ModelTrackBox): box is ModelVideoTrackBox {
  switch (box.type) {
    case 'video':
    case 'image': return true;
    case 'audio': return false;
  }
}


export function getModelTrackBoxDuration(box: ModelTrackBox): Fraction {
  return subtractFractions(box.outPointInSeconds, box.inPointInSeconds);
}


export function getModelTrackBoxesDuration(boxes: ModelTrackBox[]): Fraction {
  let result: Fraction = { numerator: 0, denominator: 1 };
  for (const box of boxes) {
    result =  addFractions(result, getModelTrackBoxDuration(box));
  }
  return result;
}
